import { Component, OnInit } from '@angular/core'
import { FormControl, FormGroup } from '@angular/forms'
import { GlobalVariables, Invoice, InvoiceStatus, Merchant, SubscriptionType } from '@parkupp/core'
import { InvoiceService } from '../../../services/invoice.service'
import { MerchantService } from '../../../services/merchant.service'
import { GlobalVariablesService } from '../../../services/global-variables.service'
import { firstValueFrom } from 'rxjs'

interface CommissionBucket {
    rate: number
    feeAmount: number
    invoiceCount: number
    invoicedAmount: number
}

interface DashboardMetrics {
    totalInvoicedAmount: number
    totalInvoicedCount: number
    totalPaidAmount: number
    totalPaidCount: number
    totalDueAmount: number
    totalDueCount: number
    totalOverdueAmount: number
    totalOverdueCount: number
    totalCancelledAmount: number
    totalCancelledCount: number
    totalRefundedAmount: number
    totalRefundedCount: number
    totalBreechedAmount: number
    totalBreechedCount: number
    totalServiceFee: number
    serviceFeeByRate: Map<number, CommissionBucket>
    totalGatewayFees: number
    gatewayFeesByType: { paygate: number; eft: number; other: number }
    grossProfit: number
    potentialServiceFee: number
    potentialGatewayFees: number
    potentialGrossProfit: number
}

@Component({
    selector: 'app-admin-monthly-dashboard',
    templateUrl: './admin-monthly-dashboard.component.html',
    styleUrls: ['./admin-monthly-dashboard.component.scss'],
})
export class AdminMonthlyDashboardComponent implements OnInit {
    isLoading = true
    metrics: DashboardMetrics = this.emptyMetrics()
    showPotentialProfit = false

    globalVariables: GlobalVariables

    filterForm: FormGroup = new FormGroup({
        filterDateRange: new FormControl('thismonth'),
        customStartDate: new FormControl(''),
        customEndDate: new FormControl(''),
    })

    dateRangeOptions = [
        { value: 'thismonth', label: 'This month' },
        { value: 'lastmonth', label: 'Last month' },
        { value: 'last7days', label: 'Last 7 days' },
        { value: 'last30days', label: 'Last 30 days' },
        { value: 'last3months', label: 'Last 3 months' },
        { value: 'last6months', label: 'Last 6 months' },
        { value: 'thisyear', label: 'This year' },
        { value: 'last12months', label: 'Last 12 months' },
        { value: 'alltime', label: 'All time' },
        { value: 'custom', label: 'Custom range' },
    ]

    private merchantMap = new Map<string, Merchant>()

    constructor(
        private invoiceService: InvoiceService,
        private merchantService: MerchantService,
        private globalVariablesService: GlobalVariablesService
    ) {}

    async ngOnInit() {
        await this.loadMerchants()
        this.globalVariables = await this.globalVariablesService.loadGlobalVariables()
        await this.loadDashboardData()

        this.filterForm.valueChanges.subscribe(() => {
            this.loadDashboardData()
        })
    }

    get isCustomDateRange(): boolean {
        return this.filterForm.get('filterDateRange')?.value === 'custom'
    }

    getDateRangeStart(dateRange: string): Date | null {
        const now = new Date()
        switch (dateRange) {
            case 'thismonth':
                return new Date(now.getFullYear(), now.getMonth(), 1)
            case 'lastmonth':
                return new Date(now.getFullYear(), now.getMonth() - 1, 1)
            case 'last7days':
                return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
            case 'last30days':
                return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
            case 'last3months':
                return new Date(now.getFullYear(), now.getMonth() - 3, now.getDate())
            case 'last6months':
                return new Date(now.getFullYear(), now.getMonth() - 6, now.getDate())
            case 'last12months':
                return new Date(now.getFullYear(), now.getMonth() - 12, now.getDate())
            case 'thisyear':
                return new Date(now.getFullYear(), 0, 1)
            case 'custom':
                const customStart = this.filterForm.get('customStartDate')?.value
                return customStart ? new Date(customStart) : null
            case 'alltime':
            default:
                return null
        }
    }

    getDateRangeEnd(dateRange: string): Date | null {
        if (dateRange === 'lastmonth') {
            const now = new Date()
            const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0)
            endOfLastMonth.setHours(23, 59, 59, 999)
            return endOfLastMonth
        }
        if (dateRange === 'custom') {
            const customEnd = this.filterForm.get('customEndDate')?.value
            if (customEnd) {
                const endDate = new Date(customEnd)
                endDate.setHours(23, 59, 59, 999)
                return endDate
            }
        }
        return null
    }

    private async loadMerchants() {
        const merchants = await firstValueFrom(this.merchantService.listAll())
        this.merchantMap.clear()
        for (const merchant of merchants) {
            if (merchant.$key) {
                this.merchantMap.set(merchant.$key, merchant)
            }
        }
    }

    async loadDashboardData() {
        this.isLoading = true

        const filterDateRange = this.filterForm.get('filterDateRange')?.value
        const fromDate = this.getDateRangeStart(filterDateRange) ?? undefined
        const toDate = this.getDateRangeEnd(filterDateRange) ?? undefined

        const invoiceDocs = await firstValueFrom(this.invoiceService.listNoLimit(fromDate, toDate))

        const paygateRate = (this.globalVariables?.paygateGatewayFeePercentage ?? 3.5) / 100
        const eftRate = (this.globalVariables?.eftGatewayFeePercentage ?? 2) / 100
        const now = new Date()

        const metrics = this.emptyMetrics()

        for (const doc of invoiceDocs) {
            const data = doc.data()
            const amount = data['amountTotalDue'] || 0

            metrics.totalInvoicedAmount += amount
            metrics.totalInvoicedCount++

            const merchantKey = data['merchantRef']?.id
            const merchant = merchantKey ? this.merchantMap.get(merchantKey) : null
            const commissionPct = Number(merchant?.commissionPercentage ?? 15)
            const commissionRate = commissionPct / 100

            const paymentType = data['paymentMethodType'] as SubscriptionType
            const invoiceGatewayRate = paymentType === SubscriptionType.EFT ? eftRate : paygateRate

            metrics.potentialServiceFee += amount * commissionRate
            metrics.potentialGatewayFees += amount * invoiceGatewayRate

            if (data['status'] === InvoiceStatus.DUE) {
                const dueDate = data['dueDate']
                const dueDateMs = dueDate?.seconds ? dueDate.seconds * 1000 : dueDate?.toMillis?.() ?? null
                if (dueDateMs && dueDateMs < now.getTime()) {
                    metrics.totalOverdueAmount += amount
                    metrics.totalOverdueCount++
                } else {
                    metrics.totalDueAmount += amount
                    metrics.totalDueCount++
                }
            }

            if (data['status'] === InvoiceStatus.CANCELLED) {
                metrics.totalCancelledAmount += amount
                metrics.totalCancelledCount++
            } else if (data['status'] === InvoiceStatus.REFUNDED) {
                metrics.totalRefundedAmount += amount
                metrics.totalRefundedCount++
            } else if (data['status'] === InvoiceStatus.BREECHED) {
                metrics.totalBreechedAmount += amount
                metrics.totalBreechedCount++
            }

            if (data['status'] === InvoiceStatus.PAID) {
                metrics.totalPaidAmount += amount
                metrics.totalPaidCount++

                const fee = amount * commissionRate
                metrics.totalServiceFee += fee

                const bucket = metrics.serviceFeeByRate.get(commissionPct) ?? { rate: commissionPct, feeAmount: 0, invoiceCount: 0, invoicedAmount: 0 }
                bucket.feeAmount += fee
                bucket.invoiceCount++
                bucket.invoicedAmount += amount
                metrics.serviceFeeByRate.set(commissionPct, bucket)

                const gatewayFee = amount * invoiceGatewayRate
                metrics.totalGatewayFees += gatewayFee
                if (paymentType === SubscriptionType.EFT) {
                    metrics.gatewayFeesByType.eft += gatewayFee
                } else if (paymentType === SubscriptionType.PAYGATE || paymentType === SubscriptionType.PAYFAST) {
                    metrics.gatewayFeesByType.paygate += gatewayFee
                } else {
                    metrics.gatewayFeesByType.other += gatewayFee
                }
            }
        }

        metrics.grossProfit = metrics.totalServiceFee - metrics.totalGatewayFees
        metrics.potentialGrossProfit = metrics.potentialServiceFee - metrics.potentialGatewayFees

        this.metrics = metrics
        this.isLoading = false
    }

    get serviceFeeBreakdown(): CommissionBucket[] {
        return Array.from(this.metrics.serviceFeeByRate.values()).sort((a, b) => b.rate - a.rate)
    }

    private emptyMetrics(): DashboardMetrics {
        return {
            totalInvoicedAmount: 0,
            totalInvoicedCount: 0,
            totalPaidAmount: 0,
            totalPaidCount: 0,
            totalDueAmount: 0,
            totalDueCount: 0,
            totalOverdueAmount: 0,
            totalOverdueCount: 0,
            totalCancelledAmount: 0,
            totalCancelledCount: 0,
            totalRefundedAmount: 0,
            totalRefundedCount: 0,
            totalBreechedAmount: 0,
            totalBreechedCount: 0,
            totalServiceFee: 0,
            serviceFeeByRate: new Map<number, CommissionBucket>(),
            totalGatewayFees: 0,
            gatewayFeesByType: { paygate: 0, eft: 0, other: 0 },
            grossProfit: 0,
            potentialServiceFee: 0,
            potentialGatewayFees: 0,
            potentialGrossProfit: 0,
        }
    }

    get dateRangeLabel(): string {
        const filterDateRange = this.filterForm.get('filterDateRange')?.value
        const option = this.dateRangeOptions.find((o) => o.value === filterDateRange)
        return option?.label || ''
    }
}
